import React, {createContext, PropsWithChildren, useMemo, useContext, useCallback} from 'react';
import {OpportunityService, OpportunityServiceImpl, OpportunitySearchParams} from '../services';
import {useAuth} from './auth.provider';
import {
  Invitation,
  OpportunityCreateServiceParams,
  RecommendedCandidatesParams,
  RecommendedCandidatesResponse,
} from '../services/opportunity';
import {CreateOpportunityParams, Opportunity} from '../models/opportunity';
import {JobApplication, JobApplicationStatus} from '../models/job-application';

export interface OpportunityProviderInterface {
  getRecommendedCandidates(opportunityId: string, params?: RecommendedCandidatesParams): Promise<RecommendedCandidatesResponse>;
  inviteCandidate: (opportunityId: string, candidateAccountId: string) => Promise<void>;
  listInvitations: (opportunityId: string, candidateAccountId?: string) => Promise<Invitation[]>;
  applyToOpportunity: (opportunityId: string) => Promise<{success: boolean}>;
  createOpportunity: (params: CreateOpportunityParams) => Promise<Opportunity>;
  deleteOpportunity: (opportunityId: string) => Promise<void>;
  getAllOpportunities: (
    searchParams?: OpportunitySearchParams,
  ) => Promise<{data: Opportunity[]; total: number; page: number; limit: number; totalPages: number}>;
  getOpportunityById: (opportunityId: string) => Promise<Opportunity>;
  opportunityService: OpportunityService;
  updateOpportunity: (opportunityId: string, updates: CreateOpportunityParams) => Promise<Opportunity>;
  getJobApplications(
    opportunityId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: string;
      search?: string;
    },
  ): Promise<{
    data: JobApplication[];
    total: number;
    hasMore: boolean;
    page: number;
    totalPages: number;
  }>;
  updateApplicationStatus: (opportunityId: string, applicationId: string, status: JobApplicationStatus) => Promise<JobApplication>;
}

export const OpportunityContext = createContext<OpportunityProviderInterface>(undefined as any as OpportunityProviderInterface);

export const useOpportunity = () => {
  const context = useContext(OpportunityContext);
  if (!context) {
    throw new Error('useOpportunity must be used within an OpportunityProvider');
  }
  return context;
};

export const OpportunityProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const opportunityService: OpportunityService = useMemo(() => new OpportunityServiceImpl(), []);
  const {user, organizationInfo} = useAuth();

  const applyToOpportunity = useCallback(
    async (opportunityId: string): Promise<{success: boolean}> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }
      const response = await opportunityService.applyToOpportunity({
        accountId: user.id,
        opportunityId,
      });
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to apply to opportunity');
      }
      return response.data;
    },
    [opportunityService, user?.id],
  );

  const getRecommendedCandidates = useCallback(
    async (opportunityId: string, params?: RecommendedCandidatesParams): Promise<RecommendedCandidatesResponse> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }
      const response = await opportunityService.getRecommendedCandidates(user.id, opportunityId, params);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to load recommended candidates.');
      }
      return response.data;
    },
    [opportunityService, user?.id],
  );

  const inviteCandidate = useCallback(
    async (opportunityId: string, candidateAccountId: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }
      const response = await opportunityService.inviteCandidate(user.id, opportunityId, candidateAccountId);
      if (response.hasError()) {
        throw new Error(response.error || 'Failed to send invitation.');
      }
    },
    [opportunityService, user?.id],
  );

  const listInvitations = useCallback(
    async (opportunityId: string, candidateAccountId?: string): Promise<Invitation[]> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }
      const response = await opportunityService.listInvitations(user.id, opportunityId, candidateAccountId);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to load invitations.');
      }
      return response.data.data ?? [];
    },
    [opportunityService, user?.id],
  );

  const getJobApplications = useCallback(
    async (
      opportunityId: string,
      params?: {
        page?: number;
        limit?: number;
        status?: string;
        search?: string;
      },
    ): Promise<{
      data: JobApplication[];
      total: number;
      hasMore: boolean;
      page: number;
      totalPages: number;
    }> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }
      const response = await opportunityService.getJobApplications(user.id, opportunityId, params);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to apply to opportunity');
      }
      return response.data;
    },
    [opportunityService, user?.id],
  );

  const createOpportunity = useCallback(
    async (params: CreateOpportunityParams): Promise<Opportunity> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }
      if (!organizationInfo?.organization?.id) {
        throw new Error('Organization ID not found in context');
      }
      const createParams: OpportunityCreateServiceParams = {
        ...params,
        accountId: user.id,
        organizationId: organizationInfo.organization.id,
      };
      const response = await opportunityService.createOpportunity(createParams);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to create opportunity');
      }
      return response.data;
    },
    [opportunityService, user?.id, organizationInfo?.organization?.id],
  );

  const deleteOpportunity = useCallback(
    async (opportunityId: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }
      if (!organizationInfo?.organization?.id) {
        throw new Error('Organization ID not found in context');
      }
      const response = await opportunityService.deleteOpportunity(user.id, opportunityId, organizationInfo.organization.id);
      if (response.hasError()) {
        throw new Error(response.error || 'Failed to delete opportunity');
      }
    },
    [opportunityService, user?.id, organizationInfo?.organization?.id],
  );

  const getAllOpportunities = useCallback(
    async (
      searchParams?: OpportunitySearchParams,
    ): Promise<{data: Opportunity[]; total: number; page: number; limit: number; totalPages: number}> => {
      const response = await opportunityService.getAllOpportunities(searchParams);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to get opportunities');
      }
      return response.data;
    },
    [opportunityService],
  );

  const getOpportunityById = useCallback(
    async (opportunityId: string): Promise<Opportunity> => {
      const response = await opportunityService.getOpportunityById(opportunityId);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to get opportunity');
      }
      return response.data;
    },
    [opportunityService],
  );

  const updateOpportunity = useCallback(
    async (opportunityId: string, updates: CreateOpportunityParams): Promise<Opportunity> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }
      if (!organizationInfo?.organization?.id) {
        throw new Error('Organization ID not found in context');
      }
      const response = await opportunityService.patchOpportunity(user.id, opportunityId, organizationInfo.organization.id, updates);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to update opportunity');
      }
      return response.data;
    },
    [opportunityService, user?.id, organizationInfo?.organization?.id],
  );

  const updateApplicationStatus = useCallback(
    async (opportunityId: string, applicationId: string, status: JobApplicationStatus): Promise<JobApplication> => {
      if (!user?.id) {
        throw new Error('Account ID not found in context');
      }

      const response = await opportunityService.updateApplicationStatus(user.id, opportunityId, applicationId, status);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Failed to update application status');
      }
      return response.data;
    },
    [opportunityService, user?.id],
  );

  return (
    <OpportunityContext.Provider
      value={{
        applyToOpportunity,
        createOpportunity,
        deleteOpportunity,
        getAllOpportunities,
        getJobApplications,
        getOpportunityById,
        getRecommendedCandidates,
        inviteCandidate,
        listInvitations,
        opportunityService,
        updateOpportunity,
        updateApplicationStatus,
      }}>
      {children}
    </OpportunityContext.Provider>
  );
};
